import { Alert, Flex, Modal, Typography, message } from 'antd';
import { useState } from 'react';

import { useCreateBillingTopUpCheckoutSession } from '../../../api/generated/api';
import { apiErrorMessage, TOP_UP_BLOCKED_COPY } from '../shared/billing-format';
import { isDiscounted } from '../shared/discount';
import { defaultPackId } from '../shared/packs';

import { DiscountedPrice } from './DiscountedPrice';
import { PackSelect } from './PackSelect';

import type { GetBillingOverviewResponseDto } from '../../../api/generated/model/getBillingOverviewResponseDto';

const { Text } = Typography;

type TopUp = NonNullable<GetBillingOverviewResponseDto['topUp']>;

type Props = {
  open: boolean;
  organizationId: string;
  topUp: TopUp | null;
  /** The pack to preselect: the one auto-recharge buys, which is the last one bought. */
  preferredPriceId?: string | null;
  onClose: () => void;
};

/**
 * A one-time top-up (UC-12): the CreditsCard button, `?buy=1` on Settings → Billing, and the
 * banner / AI chat "Buy credits" actions all land here. The manager picks a pack; confirming opens
 * a Stripe Checkout, where a promotion code can be entered; Stripe sends the manager back to
 * `/billing/return?kind=top_up`. The pack bought becomes the one auto-recharge buys.
 */
export const TopUpModal = ({ open, organizationId, topUp, preferredPriceId, onClose }: Props) => {
  const checkoutMutation = useCreateBillingTopUpCheckoutSession();
  const blocked = topUp?.blockedReason ?? null;
  const packs = topUp?.packs ?? [];
  const [picked, setPicked] = useState<string | null>(null);
  const selected = packs.find((pack) => pack.priceId === (picked ?? defaultPackId(packs, preferredPriceId)));

  const handleBuy = async () => {
    if (!selected) return;
    try {
      const result = await checkoutMutation.mutateAsync({
        organizationId,
        data: { priceId: selected.priceId },
      });
      window.location.assign(result.data.url);
    } catch (err) {
      message.error(apiErrorMessage(err, 'Failed to start checkout. Please try again.'));
    }
  };

  return (
    <Modal
      title="Buy credits"
      open={open}
      onCancel={onClose}
      okText="Continue to checkout"
      onOk={handleBuy}
      okButtonProps={{ disabled: !selected || blocked !== null, 'data-testid': 'top-up-checkout' }}
      confirmLoading={checkoutMutation.isPending}
      destroyOnHidden
    >
      {!selected ? (
        <Alert type="info" showIcon title="Top-ups are not available right now." />
      ) : (
        <Flex vertical gap="middle" style={{ paddingTop: 8 }}>
          <Flex align="center" justify="space-between" gap="small" wrap="wrap">
            <PackSelect packs={packs} value={selected.priceId} onChange={setPicked} testId="top-up-pack" />
            <DiscountedPrice
              cents={selected.priceCents}
              discountedCents={selected.discountedPriceCents}
              className="font-mono"
              style={{ fontSize: 20, fontWeight: 600 }}
              testId="top-up-price"
            />
          </Flex>
          <Text type="secondary">
            Top-up credits never expire. We use your monthly credits first, so your top-ups are
            saved.{' '}
            {isDiscounted(selected.priceCents, selected.discountedPriceCents)
              ? 'Your discount is applied at checkout.'
              : 'Have a promotion code? Enter it on the Stripe page.'}
          </Text>
          {blocked && <Alert type="warning" showIcon title={TOP_UP_BLOCKED_COPY[blocked]} />}
        </Flex>
      )}
    </Modal>
  );
};
